<template>
  <div v-for="mainItem in menuItems" :key="mainItem.id" :class="$style.mainContainer">
    <div v-if="mainItem.submenuItems">
      <HgCollapsible
        startUnfolded
        theme="light"
        iconSize="large"
        :triggerCssClass="$style.collapsibleTrigger"
        :triggerCssClassFolded="
          menuItemsHighlightedState[mainItem.id]?.shouldBeHighlighted
            ? $style.highlightedMenuItem
            : ''
        "
        :iconCssClass="$style.iconCssClass"
        @expanded="handleExpand(true, mainItem)"
        @collapsed="handleExpand(false, mainItem)"
      >
        <template #header>
          <div :class="$style.header">
            <HgIcon :icon="mainItem.icon as Icon" theme="light" size="large" />
            <span>{{ mainItem.label }}</span>
          </div>
        </template>
        <ul :class="$style.menu">
          <li
            v-for="item in mainItem.submenuItems"
            :key="item.id"
            :data-cy="item.id"
            :class="[
              $style.item,
              { [$style.active]: isMenuItemActive(item) },
              { [$style.activesubpage]: isMenuItemSubPageActive(item) },
              { [$style.disabled]: item.disabled },
            ]"
          >
            <template v-if="!item.disabled">
              <a
                v-if="item.href"
                :href="item.href"
                :class="$style.subMenuLink"
                :target="item.external ? '_blank' : undefined"
                :rel="item.external ? 'noopener' : undefined"
                @click="emitMenuItemClickEvent(item, $event)"
              >
                <div :class="$style.external">
                  <HgIcon
                    v-if="isIconName(item.icon!)"
                    theme="current"
                    :icon="item.icon as Icon"
                    size="large"
                  />
                  <component :is="item.icon" v-else :class="$style.icon" />
                  <span>{{ item.label }}</span>
                </div>
                <HgBadge
                  v-if="item.badge"
                  :text="item.badge"
                  theme="newfeature"
                  :class="$style.badge"
                />
                <HgIcon
                  v-if="item.external"
                  size="large"
                  icon="ExternalLink"
                  theme="light"
                  aria-hidden="true"
                />
              </a>
              <router-link
                v-else
                :to="item.named || item.to!"
                :class="$style.subMenuLink"
                @click="emitMenuItemClickEvent(item, $event)"
              >
                <HgIcon
                  v-if="isIconName(item.icon!)"
                  theme="current"
                  :icon="item.icon as Icon"
                  size="large"
                />
                <component :is="item.icon" v-else :class="$style.icon" />
                <span>{{ item.label }}</span>
                <HgBadge
                  v-if="item.badge"
                  :text="item.badge"
                  theme="newfeature"
                  :class="$style.badge"
                />
              </router-link>
            </template>
            <div v-else :class="$style.subMenuLink">
              <span>{{ item.label }}</span>
              <HgBadge
                v-if="item.badge"
                :text="item.badge"
                theme="newfeature"
                :class="$style.badge"
              />
              <HgIcon
                v-if="item.external"
                :class="$style.icon"
                icon="ExternalLink"
                theme="light"
                aria-hidden="true"
                size="large"
              />
            </div>
          </li>
        </ul>
      </HgCollapsible>
    </div>
    <div v-else>
      <ul :class="[$style.menu, $style.nonCollapsableMenu]">
        <li
          :key="mainItem.id"
          :data-cy="mainItem.id"
          :class="[
            $style.item,
            { [$style.active]: isMenuItemActive(mainItem) },
            { [$style.activesubpage]: isMenuItemSubPageActive(mainItem) },
            { [$style.disabled]: mainItem.disabled },
          ]"
        >
          <template v-if="!mainItem.disabled">
            <a
              v-if="mainItem.href"
              :href="`${mainItem.href}`"
              :class="$style.externalLink"
              :target="mainItem.external ? '_blank' : undefined"
              :rel="mainItem.external ? 'noopener' : undefined"
              @click="emitMenuItemClickEvent(mainItem, $event)"
            >
              <div :class="$style.external">
                <HgIcon
                  v-if="isIconName(mainItem.icon!)"
                  theme="current"
                  :icon="mainItem.icon as Icon"
                  size="large"
                  :class="$style.externalLinkTitleIcon"
                />
                <component :is="mainItem.icon" v-else :class="$style.icon" />
                <span>{{ mainItem.label }}</span>
              </div>
              <HgIcon
                v-if="mainItem.external"
                icon="ExternalLink"
                theme="light"
                aria-hidden="true"
                size="large"
              />
            </a>
            <router-link
              v-else
              :to="mainItem.named || mainItem.to!"
              :class="$style.link"
              @click="emitMenuItemClickEvent(mainItem, $event)"
            >
              <HgIcon
                v-if="isIconName(mainItem.icon!)"
                theme="current"
                :icon="mainItem.icon as Icon"
                size="large"
                :class="$style.externalLinkTitleIcon"
              />
              <component :is="mainItem.icon" v-else :class="$style.icon" />
              <span>{{ mainItem.label }}</span>
            </router-link>
          </template>
          <div v-else :class="$style.link">
            <span>{{ mainItem.label }}</span>
            <HgIcon
              v-if="mainItem.external"
              :class="$style.icon"
              icon="ExternalLink"
              theme="light"
              aria-hidden="true"
              size="large"
            />
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { HgBadge, HgCollapsible, HgIcon, Icon } from '@smg-real-estate/domus';
import { reactive, watch, type FunctionalComponent, type PropType } from 'vue';
import { type RouteLocationAsRelativeGeneric, type RouteLocationNormalized } from 'vue-router';

import type { ISidebarMenuItem } from './types';

export interface MenuItemsHighlightedState {
  isCollapsed: boolean;
  shouldBeHighlighted: boolean;
}

const { menuItems, currentRoute } = defineProps({
  /**
   * An array with the main menu items
   */
  menuItems: {
    type: Array as PropType<ISidebarMenuItem[]>,
    default: () => [],
  },
  /**
   * Current route
   */
  currentRoute: {
    type: Object as () => RouteLocationNormalized,
    required: true,
  },
});

const menuItemsHighlightedState = reactive<Record<string, MenuItemsHighlightedState>>({});

const handleRouteChange = () => {
  // Find the menu item corresponding to the current route
  const chosenMenuItem = menuItems.find((item) =>
    item.submenuItems?.some(
      (submenuItem) => isMenuItemActive(submenuItem) || isMenuItemSubPageActive(submenuItem),
    ),
  );

  // Reset all highlights if no menu item matches the route
  if (!chosenMenuItem) {
    resetAllHighlights();
    return;
  }

  // Update highlights: Only highlight the chosen menu item if it is collapsed
  Object.entries(menuItemsHighlightedState).forEach(([key, state]) => {
    state.shouldBeHighlighted = key === chosenMenuItem.id && state.isCollapsed;
  });
};

const resetAllHighlights = () => {
  Object.values(menuItemsHighlightedState).forEach((state) => {
    state.shouldBeHighlighted = false;
  });
};

const handleExpand = (isExpanded: boolean, menuItem: ISidebarMenuItem) => {
  const hasActiveSubmenu =
    menuItem.submenuItems?.some((submenuItem) => isMenuItemActive(submenuItem)) || false;

  menuItemsHighlightedState[menuItem.id] = {
    isCollapsed: !isExpanded,
    shouldBeHighlighted: hasActiveSubmenu,
  };
};

const isIconName = (icon: Icon | FunctionalComponent) => {
  return typeof icon === 'string';
};

const isMenuItemActive = (item: ISidebarMenuItem): boolean => {
  /**
   * This is just a super primitive way of querying.
   * This will be revealed in the future how the different ENVs can be matched here.
   */
  if (item.named) {
    return (item.named as RouteLocationAsRelativeGeneric).name === currentRoute.name;
  }
  if (item.to) {
    return currentRoute.path.includes(item.to);
  }
  if (item.href) {
    return window.location.href.includes(item.href);
  }
  return false;
};

const isMenuItemSubPageActive = (item: ISidebarMenuItem): boolean => {
  if (!isMenuItemActive(item) && item.subpagePath) {
    return currentRoute.path.includes(item.subpagePath);
  }

  return false;
};

const emit = defineEmits(['sideNavigationClick']);

const emitMenuItemClickEvent = (menuItem: ISidebarMenuItem, event: any): void => {
  emit('sideNavigationClick', menuItem, event);
};

watch(() => currentRoute.name, handleRouteChange, { immediate: true });
</script>

<style lang="scss" module>
@import '@/assets/variables';

.mainContainer {
  .collapsibleTrigger {
    align-items: flex-start;
    padding: $space-75 $space-75 $space-25 $space-75;
    border: $border-radius-s solid transparent;
    border-radius: $border-radius-xl;
    transition: 0.2s ease-in-out;
  }

  .highlightedMenuItem {
    border: $border-radius-s solid white;
  }

  .iconCssClass {
    margin-left: auto;
    color: white;
  }

  .header {
    display: flex;
    column-gap: $space-75;
    align-items: center;
    justify-content: flex-start;
    font-weight: normal;
  }

  .nonCollapsableMenu {
    padding: 0 !important;
  }

  .menu {
    @include menu-ul-reset;

    padding: 0 20px;

    %basic-link {
      display: inline-flex;
      align-items: center;
      width: 100%;
      padding: $space-50 $space-75;
      color: $color-white;
      text-decoration: none;
      cursor: pointer;
      border-radius: $border-radius-xl;

      span {
        padding-right: $space-50;
      }
    }

    .subMenuLink {
      padding-left: $space-200;
    }

    .externalLink,
    .subMenuLink,
    .link {
      @extend %basic-link;
    }

    .link,
    .externalLink {
      padding: $space-50 $space-100 $space-50 $space-75;
    }

    .externalLinkTitleIcon {
      margin-right: $space-75;
    }

    .external {
      display: flex;
      align-items: center;
      margin-right: auto;
    }

    .badge {
      margin-left: auto;
    }

    .item {
      display: flex;
      margin-bottom: $space-50;

      &.active {
        .subMenuLink,
        .link {
          color: $color-deep-blue-100;
          background-color: $color-white;
        }

        &:hover .subMenuLink,
        &:hover .link {
          background-color: $color-deep-blue-40;
        }
      }

      /* stylelint-disable-next-line no-descending-specificity */
      &.activesubpage %basic-link {
        background-color: unset;
        border: $border-radius-s solid $color-white;
        border-radius: $border-radius-xl;
      }

      &:not(.activesubpage, .active):hover %basic-link {
        background-color: $color-deep-blue-90;
      }

      /* stylelint-disable-next-line no-descending-specificity */
      &.activesubpage:hover %basic-link {
        color: $color-deep-blue-60;
        border: $border-radius-s solid $color-deep-blue-60;
      }

      &.disabled {
        color: $color-magenta-0;
        opacity: 0.3;

        /* stylelint-disable-next-line no-descending-specificity */
        .subMenuLink,
        .link {
          cursor: not-allowed;
        }

        .link {
          margin-left: $space-50;
        }
      }
    }
  }

  .icon {
    width: $icon-size-md;
    height: $icon-size-md;

    @include color-icon(currentColor);
  }
}
</style>
